import {
    ExtendedMonitoredTv,
    MonitoredMeasure,
    NOTE_EVENT_CLAZZ_VALUE,
    NoteEvent,
    NoteEvent_NoteStatus,
    NoteEvent_NoteType,
    NoteEvent_ShareWith,
} from 'app/model/SOL49';
import React from 'react';
import { Button, Checkbox, Col, Form, Input, Popconfirm, Row } from 'antd';
import Utils from 'app/common/util/Utils';
import SelectEnum from 'app/common/input/SelectEnum';
import ConditionalRender from 'app/common/components/ConditionalRender';
import TimeUtils from 'app/common/util/TimeUtils';
import moment from 'moment';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import {
    MonitoredEventForm,
    MonitoredEventFormProps,
    MonitoredEventFormState,
} from 'app/components/panels/predict/MonitoredEventForm';
import memoizeOne from 'memoize-one';

type NoteFormValues = Omit<NoteEvent, 'validity'> & { validity: moment.Moment[] };

interface NoteFormProps extends MonitoredEventFormProps<NoteEvent> {
    measures: MonitoredMeasure[];
    fmps?: Set<string>;
    tvs?: ExtendedMonitoredTv[];
}

interface NoteFormState extends MonitoredEventFormState {
    fmpSelectOpen: boolean;
    tvSelectOpen: boolean;
    measureSelectOpen: boolean;
}

export class NoteForm extends MonitoredEventForm<NoteEvent, NoteFormValues, NoteFormProps, NoteFormState> {
    constructor(props) {
        super(props);

        this.state = { ...this.state, fmpSelectOpen: false, tvSelectOpen: false, measureSelectOpen: false };
    }

    render() {
        return (
            <Form
                ref={this.formRef}
                style={{ width: NoteForm.FORM_WIDTH_EM }}
                layout="vertical"
                initialValues={this.defaultValues()}
                onValuesChange={() =>
                    this.setState({
                        updateDisabled: this.isUpdateDisabled(),
                        createDisabled: this.isCreateDisabled(),
                    })
                }>
                <Form.Item name="clazz" hidden>
                    <Input type="hidden" />
                </Form.Item>
                <Form.Item name="id" hidden>
                    <Input type="hidden" />
                </Form.Item>
                <Row gutter={8}>
                    <Col span={12}>
                        <Form.Item label="Visibility" name="internalOnly" valuePropName="checked">
                            <Checkbox>For internal purpose only</Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Share with" name="share">
                            <Checkbox.Group options={Object.keys(NoteEvent_ShareWith)} value={this.props.event.share} />
                        </Form.Item>
                    </Col>
                </Row>
                {MonitoredEventForm.FORM_TITLE_ITEM}
                {this.FORM_SELECT_ITEM(
                    'FMP',
                    'fmps',
                    this.getOptionsFromFmps(this.props.fmps),
                    this.state.fmpSelectOpen,
                    open => this.setState({ fmpSelectOpen: open }),
                )}
                {this.FORM_SELECT_ITEM(
                    'TFV',
                    'tvs',
                    this.getOptionsFromMonitoredTvs(this.props.tvs),
                    this.state.tvSelectOpen,
                    open => this.setState({ tvSelectOpen: open }),
                )}
                {this.FORM_SELECT_ITEM(
                    'Measure',
                    'measures',
                    this.getOptionsFromMeasures(this.props.measures),
                    this.state.measureSelectOpen,
                    open => this.setState({ measureSelectOpen: open }),
                )}
                <Row gutter={8}>
                    <Col span={12}>
                        <Form.Item label="Type" name="type">
                            <SelectEnum enumType={NoteEvent_NoteType} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Status" name="status">
                            <SelectEnum enumType={NoteEvent_NoteStatus} />
                        </Form.Item>
                    </Col>
                </Row>
                {MonitoredEventForm.FORM_VALIDITY_ITEM}
                <Form.Item label="Attention" name="attention">
                    <Input />
                </Form.Item>
                {MonitoredEventForm.FORM_DESCRIPTION_ITEM(4, 'Remarks/Advice')}
                <Row gutter={8}>
                    <Col span={this.isNewEvent() ? 20 : 16}>
                        <ConditionalRender condition={Utils.isExists(this.props.event?.lastUpdate)}>
                            <span>Last update: {TimeUtils.formatDateHHMM(this.props.event?.lastUpdate)}</span>
                        </ConditionalRender>
                    </Col>
                    <ConditionalRender condition={!this.isNewEvent()}>
                        <Col span={4}>
                            <Popconfirm
                                title="Delete the note"
                                okText="Yes"
                                cancelText="No"
                                onConfirm={this.props.onDelete}>
                                <Button>Delete</Button>
                            </Popconfirm>
                        </Col>
                    </ConditionalRender>
                    <Col span={4}>
                        <ConditionalRender condition={this.isNewEvent()}>
                            <Button onClick={this.onSubmit} disabled={this.state.createDisabled}>
                                Create
                            </Button>
                        </ConditionalRender>
                        <ConditionalRender condition={!this.isNewEvent()}>
                            <Button onClick={this.onSubmit} disabled={this.state.updateDisabled}>
                                Update
                            </Button>
                        </ConditionalRender>
                    </Col>
                </Row>
            </Form>
        );
    }

    private defaultValues = (): NoteFormValues => {
        return {
            attention: '',
            clazz: '',
            description: '',
            fmps: [],
            id: '',
            internalOnly: false,
            lastUpdate: undefined,
            measures: [],
            share: [],
            status: NoteEvent_NoteStatus.IN_PREPARATION,
            type: NoteEvent_NoteType.GEN,
            title: '',
            tvs: [],
            validity: [],
        };
    };

    private getOptionsFromFmps = memoizeOne((fmps: Set<string>) => {
        if (!fmps) return [];

        return Array.from(fmps).map(fmp => ({
            value: fmp,
            label: fmp,
        }));
    });

    private getOptionsFromMeasures = memoizeOne((measures: MonitoredMeasure[]) => {
        if (!measures) return [];

        return measures?.map(monitoredMeasure => ({
            value: monitoredMeasure.measure.measureId.id,
            label: monitoredMeasure.measure.measureId.id,
        }));
    });

    private getOptionsFromMonitoredTvs = memoizeOne((monitoredTvs: ExtendedMonitoredTv[]) => {
        if (!monitoredTvs) return [];

        return monitoredTvs.map(monitoredTv => ({
            value: monitoredTv.refLocation.id,
            label: monitoredTv.refLocation.id,
        }));
    });

    protected fromFormValues(values: NoteFormValues) {
        return {
            ...values,
            validity: TimePeriodUtils.momentToTimePeriod(values.validity),
            tvs: values.tvs || [], // Little hack since the "initialValues" doesn't set this field weirdly
        };
    }

    protected toFormValues(event: NoteEvent): NoteFormValues {
        return {
            ...event,
            validity: TimePeriodUtils.timePeriodToMoment(event.validity),
            clazz: NOTE_EVENT_CLAZZ_VALUE,
        };
    }

    protected isUpdateDisabled(): boolean {
        if (!this.formRef.current?.getFieldsValue() || !this.props.event) return true;
        const values: NoteFormValues = this.formRef.current.getFieldsValue();
        const event = this.props.event;

        return (
            super.isUpdateDisabled() &&
            values.description === event.description &&
            values.internalOnly === event.internalOnly &&
            Utils.isShallowEquals(values.share, event.share) &&
            values.attention === event.attention &&
            values.status === event.status &&
            values.type === event.type &&
            Utils.isShallowEquals(values.tvs, event.tvs) &&
            Utils.isShallowEquals(values.fmps, event.fmps) &&
            Utils.isShallowEquals(values.measures, event.measures)
        );
    }
}
